﻿import { Job } from "@ethossoftworks/job"
import { BlocCoordinator } from "@lib/bloc/BlocCoordinator"
import { ServiceItemForm, ServiceItemTotals, serviceItemsTotals } from "@model/serviceRequests/ServiceItemForm"
import { TagChange } from "@model/Tag"
import { WorkRequest } from "@model/workRequests/WorkRequest"
import { serviceItemFormToWorkRequest } from "@service/workRequests/MainWorkRequestService"
import { StatusBloc } from "@state/StatusBloc"
import { UserBloc } from "@state/user/UserBloc"
import { CreateNewWorkOrderScreenBloc } from "@state/workOrders/CreateNewWorkOrderModalBloc"
import { ServiceItemsBloc, ServiceItemsListForm } from "@state/workRequests/ServiceItemsBloc"
import { DI } from "@ui/DI"
import { ServiceItemViewModel } from "@ui/workRequests/ServiceItemViewModel"
import { WorkOrderStatus } from "@model/workOrders/WorkOrder"

type StateSelection = ServiceItemsListForm & {
    totals: ServiceItemTotals
    count: number
}

export class ServiceItemsViewModel extends BlocCoordinator<[ServiceItemsListForm], StateSelection> {
    private itemViewModels = new Map<number, ServiceItemViewModel>()

    constructor(
        private serviceItemsBloc: ServiceItemsBloc,
        private statusBloc: StatusBloc,
        private userBloc: UserBloc,
        private createNewWorkOrderScreenBloc: CreateNewWorkOrderScreenBloc
    ) {
        super([serviceItemsBloc])
    }

    transform = ([form]: [ServiceItemsListForm]): StateSelection => ({
        ...form,
        totals: serviceItemsTotals(form.items),
        count: form.items.length,
    })

    addItem = () => this.serviceItemsBloc.addItem()

    addExistingItem = async (item: ServiceItemForm) => {
        this.createNewWorkOrderScreenBloc.setIsLoading(true)
        await this.serviceItemsBloc.addExistingItem(item)
        this.createNewWorkOrderScreenBloc.setIsLoading(false)
    }

    updateItem = (item: ServiceItemForm) => {
        this.serviceItemsBloc.updateItem(item)
    }

    deleteItem = (id: number) => {
        this.serviceItemsBloc.deleteItem(id)
    }

    /** Get a viewmodel for a specific item */
    getItemViewModel = (id: number): ServiceItemViewModel | undefined => {
        if (!this.itemViewModels.has(id)) {
            const bloc = this.serviceItemsBloc.getItemBloc(id)
            if (!bloc) return undefined

            const vm = new ServiceItemViewModel(
                bloc,
                DI.userBloc(),
                DI.companyBloc(),
                DI.statusBloc(),
                DI.createNewWorkOrderScreenBloc()
            )

            //Notify parent whenever the child changes
            vm.setOnChangeListener((form) => {
                this.updateItem(form)
            })

            this.itemViewModels.set(id, vm)
        }

        return this.itemViewModels.get(id)
    }

    isWorkOrderNotClosed = () => {
        return this.createNewWorkOrderScreenBloc.state.workOrder?.status !== WorkOrderStatus.Closed
    }

    isEditingAnExistingItemOnAWorkOrder = (item: ServiceItemForm) => {
        return (
            (item.id ?? 0) > 0 &&
            (item.workOrderId ?? 0) > 0 &&
            (this.createNewWorkOrderScreenBloc.state.workOrder?.id ?? 0) > 0
        )
    }

    removeServiceItemClicked = (serviceItem: ServiceItemForm) => {
        this.serviceItemsBloc.workRequestToRemoveChanged(serviceItemFormToWorkRequest(serviceItem))
    }

    removeWorkRequestCancelClicked = () => this.serviceItemsBloc.workRequestToRemoveChanged(null)

    removeWorkRequestConfirmClicked = async (workRequest: WorkRequest) => {
        this.serviceItemsBloc.workRequestToRemoveChanged(null)
        const infoMessage = this.statusBloc.enqueueInfoMessage("Declining service item...")
        const outcome = await this.serviceItemsBloc.removeWorkRequest(workRequest)
        this.statusBloc.hideInfoMessage(infoMessage)
    }

    dismissWorkRequestClicked = (serviceItem: ServiceItemForm) =>
        this.serviceItemsBloc.workRequestToDismissChanged(
            serviceItemFormToWorkRequest(serviceItem),
            this.createNewWorkOrderScreenBloc.state.asset,
            {}
        )

    dismissWorkRequestReasonChanged = (workRequest: WorkRequest, reason: string) =>
        this.serviceItemsBloc.workRequestToDismissChanged(workRequest, this.createNewWorkOrderScreenBloc.state.asset, {
            reason: reason,
        })

    dismissWorkRequestTagChanged = (workRequest: WorkRequest, change: TagChange) =>
        this.serviceItemsBloc.workRequestToDismissChanged(workRequest, this.createNewWorkOrderScreenBloc.state.asset, {
            tagChange: change,
        })

    dismissWorkRequestTagReasonChanged = (workRequest: WorkRequest, tagReason: string) =>
        this.serviceItemsBloc.workRequestToDismissChanged(workRequest, this.createNewWorkOrderScreenBloc.state.asset, {
            tagReason: tagReason,
        })

    dismissWorkRequestCancelClicked = () =>
        this.serviceItemsBloc.workRequestToDismissChanged(null, this.createNewWorkOrderScreenBloc.state.asset, {})

    dismissWorkRequestConfirmClicked = async () => {
        const data = this.state.workRequestToDismiss
        if (data === null) return

        this.serviceItemsBloc.workRequestToDismissChanged(null, this.createNewWorkOrderScreenBloc.state.asset, {})
        const infoMessage = this.statusBloc.enqueueInfoMessage("Deleting service item...")

        var updatedAsset = await this.serviceItemsBloc.updateAssetTag(
            this.createNewWorkOrderScreenBloc.state.asset!,
            data.tagChange,
            data.tagReason
        )
        if (updatedAsset !== null) {
            this.createNewWorkOrderScreenBloc.assetChanged(updatedAsset)
        }

        const outcome = await this.serviceItemsBloc.dismissWorkRequest(
            data.request,
            data.reason,
            this.userBloc.state.user.id,
            updatedAsset ? updatedAsset.tag : null
        )

        if (!Job.isCancelled(outcome) && outcome.isError()) {
            this.statusBloc.enqueueErrorMessage("Error deleting service item")
        }
        this.statusBloc.hideInfoMessage(infoMessage)
    }
}
